import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ShieldCheck, Phone, Send, Mail } from 'lucide-react';
import Pickup from '../assets/images/data/Pickup.png';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PickupRequest = () => {
    const [formData, setFormData] = useState({
        name: '',
        phone_number: '',
        email: '',
        address: '',
        state: '',
        city: '',
        pincode: '',
        pickUpDate: '',
        pickupTime: '',
        expectedPackages: '',
        load_type: '',
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const payload = {
                company_id: 3,
                name: formData.name,
                phone_number: formData.phone_number,
                email: formData.email,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                pincode: formData.pincode,
                pickUpDate: formData.pickUpDate,
                pickupTime: {
                    from: formData.pickupTime,
                },
                expectedPackages: formData.expectedPackages,
                load_type: formData.load_type,
                makeThisSlotDefault: true,
            };

            const response = await axios.post(`${API_URL}requestPickups/create/web`, payload);

            if (response.status === 201) {
                alert("Pickup Request created successfully!");
                setFormData({}); // Clear form
            } else {
                alert("Something went wrong.");
            }

        } catch (error) {
            console.error("Error submitting form:", error);
            alert("Error creating pickup request.");
        }
    };

    return (
        <>
            <Helmet>
                <title>Pickup Request | S Infinite Express</title>
                <meta
                    name="description"
                    content="Submit a pickup request with S Infinite Express. Fast and reliable logistics support for courier pickup, parcel delivery, and freight shipping across India."
                />
                <meta
                    name="keywords"
                    content="pickup request, courier pickup India, logistics service, freight pickup, schedule courier, parcel pickup request, express pickup service, S Infinite Express, same-day pickup, pickup and delivery service, logistics pickup form, shipping request India, cargo pickup"
                />
                <meta property="og:title" content="Pickup Request | S Infinite Express" />
                <meta
                    property="og:description"
                    content="Get started with S Infinite Express logistics by requesting a pickup. Our team ensures prompt collection and smooth delivery service across India."
                />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://sinfiniteexpress.com/pickup-request" />
                <link rel="canonical" href="https://sinfiniteexpress.com/pickup-request" />
            </Helmet>

            {/* Hero Section with Background Image and Info Section */}
            <section
                className="relative bg-cover bg-center bg-no-repeat py-12 px-4 text-center text-white"
                style={{ backgroundImage: `url(${Pickup})` }}
            >

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/60"></div>

                {/* Content */}
                <div className="relative z-10 max-w-5xl mx-auto">
                    <h2 className="text-4xl md:text-5xl font-extrabold text-[#fff44f] mb-4 drop-shadow-lg">
                        Need a Pickup? We've Got You!
                    </h2>
                    <div className="w-24 h-1 bg-yellow-400 mx-auto rounded-full mb-6"></div>
                    <p className="text-lg text-gray-100 max-w-2xl mx-auto drop-shadow-sm">
                        Schedule a pickup with <span className="font-semibold text-[#fff44f]">S Infinite Express</span> and experience fast, reliable, and seamless logistics at your doorstep.
                    </p>
                </div>

                {/* Info Section */}
                <div className="relative z-10 mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-6xl mx-auto px-4">
                    {[
                        {
                            icon: <ShieldCheck className="w-6 h-6 text-[#fff44f]" />,
                            title: "Reliable Service",
                            desc: "On-time pickups and secure deliveries nationwide.",
                        },
                        {
                            icon: <Phone className="w-6 h-6 text-[#fff44f]" />,
                            title: "Easy Booking",
                            desc: "Just fill the form — we’ll handle the rest.",
                        },
                        {
                            icon: <Mail className="w-6 h-6 text-[#fff44f]" />,
                            title: "24/7 Support",
                            desc: "Get updates and assistance anytime.",
                        },
                    ].map((item, i) => (
                        <div
                            key={i}
                            className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl shadow-lg text-left border border-white/20"
                        >
                            <div className="mb-3">{item.icon}</div>
                            <h4 className="text-lg font-semibold text-yellow-300 mb-1">
                                {item.title}
                            </h4>
                            <p className="text-sm text-gray-100">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Form Section */}
            <section className="relative bg-gradient-to-br from-yellow-50 via-white to-red-50 py-20 px-4 overflow-hidden">
                {/* Decorative Background Blobs */}
                <div className="absolute top-[-50px] left-[-50px] w-72 h-72 bg-yellow-200 opacity-20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-[-50px] right-[-50px] w-72 h-72 bg-red-300 opacity-20 rounded-full blur-3xl"></div>

                <div className="relative max-w-6xl mx-auto bg-gradient-to-r from-[#fff44f] via-[#fff86f] to-[#fffcb0] backdrop-blur-lg shadow-2xl rounded-3xl px-8 py-12">
                    <h3 className="text-3xl md:text-4xl font-bold text-left text-[#211f57] mb-10">
                        Pickup Request Form
                    </h3>

                    <form onSubmit={handleSubmit} className="space-y-12 text-sm">
                        {/* Pickup Details (Consignor) */}
                        <div>
                            <h4 className="text-xl font-bold text-[#211f57] mb-3">Pickup Details (Consignor)</h4>
                            <div className="grid md:grid-cols-4 gap-6">
                                {[
                                    { id: 'name', label: 'Full Name/Company Name*', type: 'text' },
                                    { id: 'phone_number', label: 'Phone*', type: 'tel' },
                                    { id: 'email', label: 'Email*', type: 'email' },
                                    { id: 'address', label: 'Pickup Address*', type: 'text' },
                                    { id: 'state', label: 'State*', type: 'text' },
                                    { id: 'city', label: 'City*', type: 'text' },
                                    { id: 'pincode', label: 'Pin Code*', type: 'text' },
                                ].map(({ id, label, type }) => (
                                    <div key={id}>
                                        <label htmlFor={id} className="block text-gray-700 font-semibold mb-1">{label}</label>
                                        <input
                                            id={id}
                                            name={id}
                                            type={type}
                                            value={formData[id] || ''}
                                            required={label.includes('*')}
                                            placeholder={label}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-yellow-500 shadow-sm"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Expected Pickup Date and Time */}
                        <div>
                            <h4 className="text-xl font-bold text-[#211f57] mb-6">Expected Pickup Date and Time</h4>
                            <div className="grid md:grid-cols-5 gap-6">
                                {/* Pickup Date */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">
                                        Pickup Date*
                                    </label>
                                    <input
                                        type="date"
                                        name="pickUpDate"
                                        required
                                        value={formData.pickUpDate}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-500 shadow-sm text-sm"
                                    />
                                </div>

                                {/* Pickup Time */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">
                                        Pickup Time
                                    </label>
                                    <input
                                        type="time"
                                        name="pickupTime"
                                        value={formData.pickupTime}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-500 shadow-sm text-sm"
                                    />
                                </div>

                                {/* Total Weight & Total Boxes */}
                                {[
                                    { id: 'expectedPackages', label: 'Total Boxes*', type: 'number' },
                                ].map(({ id, label, type }) => (
                                    <div key={id}>
                                        <label htmlFor={id} className="block text-gray-700 font-semibold mb-1">
                                            {label}
                                        </label>
                                        <input
                                            id={id}
                                            name={id}
                                            type={type}
                                            value={formData[id] || ''}
                                            required={label.includes('*')}
                                            placeholder={label}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-500 shadow-sm text-sm"
                                        />
                                    </div>
                                ))}

                                {/* Load Type */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">
                                        Load Type (PTL/FTL/LTL)*
                                    </label>
                                    <select
                                        name="load_type"
                                        value={formData.load_type}
                                        required
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-500 shadow-sm text-sm"
                                    >
                                        <option value="">Select Load Type</option>
                                        <option value="LTL">LTL</option>
                                        <option value="PTL">PTL</option>
                                        <option value="FTL">FTL</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="text-center mt-8">
                            <button
                                type="submit"
                                className="inline-flex items-center gap-2 px-12 py-3 bg-red-600 text-white font-semibold rounded-xl shadow-md hover:scale-105 hover:from-red-700 hover:to-yellow-600 transition-transform duration-300"
                            >
                                <Send className="w-5 h-5" /> Submit Request
                            </button>
                        </div>
                    </form>
                </div>
            </section>
        </>
    );
};

export default PickupRequest;
